import React, { useId, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Button, Spinner } from '../../primitives';
import { Spacer } from '../../helpers';
import Textarea, { isValid as isValidTextarea } from './fields/Textarea';
import InputFile, { isValid as isValidInputFile } from './fields/InputFile';
import Input, { isValid as isValidInput } from './fields/Input';
import Checkbox, { isValid as isValidCheckbox } from './fields/Checkbox';
import Matrix, { isValid as isValidMatrix } from './fields/Matrix';
import Description from './fields/parts/Description';
import FieldError from './fields/parts/FieldError';
import { colors, mixins, text } from '../../core/styles/theme/default';
import { rem } from 'polished';
import i18n from './measure-i18n';
// @ts-ignore
import { Form } from 'react-stages';

const StyledWrapper = styled.div`
  ${mixins.responsiveStyles({
    'scroll-margin-top': { zero: 76, medium: 100 },
  })}
`;

const StyledMessage = styled.p(
  ({ type }: { type: string }) => css`
    ${text.h3}

    color: ${type === 'error' ? colors.error : 'inherit'};
  `,
);

const StyledHeading = styled.p`
  ${text.h3}
  line-height: 1.5;
  padding-bottom: ${rem(6)};
  border-bottom: ${rem(1)} solid currentColor;
`;

const StyledFieldGroup = styled.div`
  display: grid;

  ${mixins.responsiveStyles({
    'grid-template-columns': {
      zero: '1fr',
      medium: 'repeat(2, 1fr)',
    },
    'grid-gap': {
      zero: `${rem(16)}`,
      medium: `${rem(24)}`,
      ultra: `${rem(24)} ${rem(32)}`,
    },
  })}
`;

const StyledField = styled.div`
  font-size: ${rem(16)};

  input,
  textarea {
    background: ${colors.white};
    box-shadow: none;
    border: ${rem(1)} solid currentColor;
    border-radius: ${rem(2)};
    min-height: ${rem(48)};
    padding: ${rem(12)};
    margin: 0;
    width: 100%;

    ${mixins.responsiveStyles({
      'scroll-margin-top': { zero: 58, medium: 82 },
    })}

    &:focus {
      outline: ${rem(2)} solid ${colors.blue800};
      outline-offset: ${rem(-1)};
    }

    &::placeholder {
      color: ${colors.grey500};
    }

    &[data-error='true'] {
      border-color: ${colors.error};
    }
  }

  textarea {
    display: block;
    resize: vertical;
    min-height: ${rem(200)};
  }
`;

const StyledFieldFull = styled(StyledField)`
  ${mixins.MQ('medium')} {
    grid-column: span 2;
  }
`;

const StyledButton = styled(Button)`
  min-width: 0;
`;

const StyledButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${rem(16)};
`;

const fields = {
  text: {
    component: Input,
    isValid: isValidInput,
  },
  url: {
    component: Input,
    isValid: isValidInput,
  },
  email: {
    component: Input,
    isValid: isValidInput,
  },
  tel: {
    component: Input,
    isValid: isValidInput,
  },
  textarea: {
    component: Textarea,
    isValid: isValidTextarea,
  },
  file: {
    component: InputFile,
    isValid: isValidInputFile,
  },
  checkbox: {
    component: Checkbox,
    isValid: isValidCheckbox,
  },
  matrix: {
    component: Matrix,
    isValid: isValidMatrix,
  },
};

const MeasureForm = ({
  saveAPI,
  uploadAPI,
  charterPageUrl = '/',
  locale = 'de',
}: {
  saveAPI: string;
  uploadAPI: string;
  charterPageUrl: string;
  locale?: string;
}) => {
  const formId = useId();
  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  const onSubmit = () => {
    setIsLoading(true);
    setMessage({ text: '', type: '' });

    fetch(saveAPI, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...data,
        locale,
      }),
    })
      .then((response) => {
        if (response.ok) {
          setMessage({ text: i18n.successMessage[locale], type: 'default' });
          setData({});

          return;
        }

        return response.json();
      })
      .then((error) => {
        if (!error) return;

        setMessage({ text: i18n.failureMessage[locale], type: 'error' });
      })
      .catch(() => {
        setMessage({ text: i18n.failureMessage[locale], type: 'error' });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <StyledWrapper>
      <form
        onSubmit={(e: React.FormEvent<HTMLFormElement>) => e.preventDefault()}
        noValidate
      >
        <Form
          id={`measure-form-${locale}`}
          data={data}
          fields={fields}
          onChange={(payload: object) => setData(payload)}
          config={{
            fields: () => [
              {
                id: 'initiatorOrganisation',
                label: i18n.organisation[locale],
                type: 'text',
                locale,
                isRequired: true,
              },
              {
                id: 'initiatorAddress',
                label: i18n.address[locale],
                type: 'text',
                locale,
                isRequired: true,
              },
              {
                id: 'initiatorPostcode',
                label: i18n.postcode[locale],
                type: 'text',
                locale,
                isRequired: true,
                customValidation: ({ data, fieldConfig }: any) => {
                  if (!data && fieldConfig.isRequired) {
                    return false;
                  }
                  if (data && !data.match(/^[1-9]\d{3}$/)) {
                    return 'INVALID_POSTCODE';
                  }
                  return true;
                },
                errorRenderer: (error: any) =>
                  FieldError({
                    text:
                      error.errorCode === 'INVALID_POSTCODE'
                        ? i18n.invalidPostcode[locale]
                        : i18n.requiredField[locale],
                  }),
              },
              {
                id: 'initiatorCity',
                label: i18n.city[locale],
                type: 'text',
                locale,
                isRequired: true,
              },
              {
                id: 'initiatorWebsite',
                label: i18n.website[locale],
                type: 'url',
                locale,
                isRequired: true,
                customValidation: ({ data, fieldConfig }: any) => {
                  if (!data && fieldConfig.isRequired) {
                    return false;
                  }
                  if (
                    data &&
                    !data.match(
                      '^(ftp|http|https)://(\\w+:{0,1}\\w*@)?(\\S+)(:[0-9]+)?(/|/([\\w#!:.?+=&%@\\-/]))?$',
                    )
                  ) {
                    return 'INVALID_URL';
                  }
                  return true;
                },
                errorRenderer: (error: any) =>
                  FieldError({
                    text:
                      error.errorCode === 'INVALID_URL'
                        ? i18n.invalidUrl[locale]
                        : i18n.requiredField[locale],
                  }),
              },
              {
                id: 'initiatorLogo',
                label: i18n.logo[locale],
                prefix: i18n.logoPrefix[locale],
                suffix: i18n.logoSuffix[locale],
                type: 'file',
                locale,
                isRequired: true,
                maxFileSize: 1024 * 1024 * 1, // 1 MiB
                allowedFileTypes: ['image/jpeg', 'image/png', 'image/svg+xml'],
                maxNumberOfFiles: 1,
                api: uploadAPI,
                formId,
              },
              {
                id: 'personName',
                label: i18n.personName[locale],
                type: 'text',
                locale,
                isRequired: true,
              },
              {
                id: 'personSurname',
                label: i18n.personSurname[locale],
                type: 'text',
                locale,
                isRequired: true,
              },
              {
                id: 'personEmail',
                label: i18n.personEmail[locale],
                type: 'email',
                locale,
                isRequired: true,
                customValidation: ({ data, fieldConfig }: any) => {
                  if (!data && fieldConfig.isRequired) {
                    return false;
                  }
                  if (
                    data &&
                    !data.match('^\\w[\\w.-]*@([\\w-]+\\.)+[\\w-]+$')
                  ) {
                    return 'INVALID_EMAIL';
                  }
                  return true;
                },
                errorRenderer: (error: any) =>
                  FieldError({
                    text:
                      error.errorCode === 'INVALID_EMAIL'
                        ? i18n.invalidEmail[locale]
                        : i18n.requiredField[locale],
                  }),
              },
              {
                id: 'personRole',
                label: i18n.personRole[locale],
                type: 'text',
                locale,
                isRequired: true,
              },
              {
                id: 'personPhone',
                label: i18n.personPhone[locale],
                type: 'tel',
                locale,
                isRequired: true,
                customValidation: ({ data, fieldConfig }: any) => {
                  if (!data && fieldConfig.isRequired) {
                    return false;
                  }
                  if (data && !data.match('^(0|\\+)[\\d- /]{9,}$')) {
                    return 'INVALID_NUMBER';
                  }
                  return true;
                },
                errorRenderer: (error: any) =>
                  FieldError({
                    text:
                      error.errorCode === 'INVALID_NUMBER'
                        ? i18n.invalidPhone[locale]
                        : i18n.requiredField[locale],
                  }),
              },
              {
                id: 'personPhone2',
                label: i18n.personPhone2[locale],
                type: 'tel',
                locale,
                isRequired: false,
                customValidation: ({ data, fieldConfig }: any) => {
                  if (!data && fieldConfig.isRequired) {
                    return false;
                  }
                  if (data && !data.match('^(0|\\+)[\\d- /]{9,}$')) {
                    return 'INVALID_NUMBER';
                  }
                  return true;
                },
                errorRenderer: (error: any) =>
                  FieldError({
                    text:
                      error.errorCode === 'INVALID_NUMBER'
                        ? i18n.invalidPhone[locale]
                        : i18n.requiredField[locale],
                  }),
              },
              {
                id: 'measureTitle',
                label: i18n.measureTitle[locale],
                prefix: i18n.measureTitlePrefix[locale],
                type: 'text',
                locale,
                isRequired: true,
                showCharactersCount: true,
                maxLength: 256,
              },
              {
                id: 'measureDescription',
                label: i18n.measureDescription[locale],
                prefix: i18n.measureDescriptionPrefix[locale],
                type: 'textarea',
                locale,
                isRequired: true,
                showCharactersCount: true,
                maxLength: 2000,
              },
              {
                id: 'measureGoals',
                label: i18n.measureGoals[locale],
                prefix: i18n.measureGoalsPrefix[locale],
                type: 'textarea',
                locale,
                isRequired: true,
                showCharactersCount: true,
                maxLength: 2000,
              },
              {
                id: 'measureContribution',
                label: i18n.measureContribution[locale],
                prefix: i18n.measureContributionPrefix[locale].replace(
                  '{CHARTER_PAGE_URL}',
                  charterPageUrl,
                ),
                type: 'matrix',
                locale,
                isRequired: true,
                groups: [
                  {
                    id: 'vehicles',
                    label: i18n.measureContributionVehiclesLabel[locale],
                    fields: [
                      {
                        id: 'usedEvMarket',
                        label:
                          i18n.measureContributionVehiclesUsedEvMarket[locale],
                      },
                      {
                        id: 'customerAwareness',
                        label:
                          i18n.measureContributionVehiclesCustomerAwareness[
                            locale
                          ],
                      },
                    ],
                  },
                  {
                    id: 'charging',
                    label: i18n.measureContributionChargingLabel[locale],
                    fields: [
                      {
                        id: 'accessRenters',
                        label:
                          i18n.measureContributionChargingAccessRenters[locale],
                      },
                      {
                        id: 'networkDensity',
                        label:
                          i18n.measureContributionChargingNetworkDensity[
                            locale
                          ],
                      },
                      {
                        id: 'customerExperience',
                        label:
                          i18n.measureContributionChargingCustomerExperience[
                            locale
                          ],
                      },
                    ],
                  },
                  {
                    id: 'powerSupply',
                    label: i18n.measureContributionPowerSupplyLabel[locale],
                    fields: [
                      {
                        id: 'marketPrices',
                        label:
                          i18n.measureContributionPowerSupplyMarketPrices[
                            locale
                          ],
                      },
                      {
                        id: 'gridExpansion',
                        label:
                          i18n.measureContributionPowerSupplyGridExpansion[
                            locale
                          ],
                      },
                      {
                        id: 'energyManagement',
                        label:
                          i18n.measureContributionPowerSupplyEnergyManagement[
                            locale
                          ],
                      },
                    ],
                  },
                ],
                options: [
                  {
                    id: 'cars',
                    label: i18n.measureContributionCategoryCars[locale],
                  },
                  {
                    id: 'vans',
                    label: i18n.measureContributionCategoryVans[locale],
                  },
                  {
                    id: 'trucks',
                    label: i18n.measureContributionCategoryTrucks[locale],
                  },
                  {
                    id: 'buses',
                    label: i18n.measureContributionCategoryBuses[locale],
                  },
                ],
              },
              {
                id: 'measurePioneeringCharacter',
                label: i18n.measurePioneeringCharacter[locale],
                prefix: i18n.measurePioneeringCharacterPrefix[locale],
                type: 'textarea',
                locale,
                isRequired: true,
                showCharactersCount: true,
                maxLength: 2000,
              },
              {
                id: 'measureDuration',
                label: i18n.measureDuration[locale],
                prefix: i18n.measureDurationPrefix[locale],
                type: 'textarea',
                locale,
                isRequired: true,
                showCharactersCount: true,
                maxLength: 2000,
              },
              {
                id: 'measureOutput',
                label: i18n.measureOutput[locale],
                prefix: i18n.measureOutputPrefix[locale],
                type: 'textarea',
                locale,
                isRequired: true,
                showCharactersCount: true,
                maxLength: 2000,
              },
              {
                id: 'measureCommunication',
                label: i18n.measureCommunication[locale],
                prefix: i18n.measureCommunicationPrefix[locale],
                type: 'textarea',
                locale,
                isRequired: true,
                showCharactersCount: true,
                maxLength: 2000,
              },
              {
                id: 'measurePitch',
                label: i18n.measurePitch[locale],
                prefix: i18n.measurePitchPrefix[locale],
                type: 'textarea',
                locale,
                isRequired: false,
                showCharactersCount: true,
                maxLength: 1000,
              },
              {
                id: 'measureImages',
                label: i18n.measureImages[locale],
                prefix: i18n.measureImagesPrefix[locale],
                type: 'file',
                locale,
                isRequired: false,
                maxFileSize: 1024 * 1024 * 3, // 3 MiB
                allowedFileTypes: ['image/jpeg', 'image/png', 'image/svg+xml'],
                maxNumberOfFiles: 2,
                api: uploadAPI,
                formId,
              },
              {
                id: 'measureComment',
                label: i18n.measureComment[locale],
                prefix: i18n.measureCommentPrefix[locale],
                type: 'textarea',
                locale,
                isRequired: false,
                showCharactersCount: true,
                maxLength: 2000,
              },
              {
                id: 'charterConfirmed',
                label: i18n.charterConfirmed[locale].replace(
                  '{CHARTER_PAGE_URL}',
                  charterPageUrl,
                ),
                type: 'checkbox',
                locale,
                isRequired: true,
              },
            ],
          }}
          render={({ actionProps, fieldProps }: any) => (
            <>
              <StyledHeading>{i18n.organisationHeading[locale]}</StyledHeading>
              <Spacer space="delta" />
              <StyledFieldGroup>
                <StyledField>
                  {fieldProps.fields.initiatorOrganisation}
                </StyledField>
                <StyledField>{fieldProps.fields.initiatorAddress}</StyledField>
                <StyledField>{fieldProps.fields.initiatorPostcode}</StyledField>
                <StyledField>{fieldProps.fields.initiatorCity}</StyledField>
                <StyledFieldFull>
                  {fieldProps.fields.initiatorWebsite}
                </StyledFieldFull>
                <StyledFieldFull>
                  {fieldProps.fields.initiatorLogo}
                </StyledFieldFull>
              </StyledFieldGroup>
              <Spacer space="giga" />
              <StyledHeading>{i18n.personHeading[locale]}</StyledHeading>
              <Spacer space="delta" />
              <StyledFieldGroup>
                <StyledField>{fieldProps.fields.personName}</StyledField>
                <StyledField>{fieldProps.fields.personSurname}</StyledField>
                <StyledField>{fieldProps.fields.personEmail}</StyledField>
                <StyledField>{fieldProps.fields.personRole}</StyledField>
                <StyledField>{fieldProps.fields.personPhone}</StyledField>
                <StyledField>{fieldProps.fields.personPhone2}</StyledField>
              </StyledFieldGroup>
              <Spacer space="giga" />
              <StyledHeading>{i18n.measureHeading[locale]}</StyledHeading>
              <Spacer space="gamma" />
              <Description>{i18n.measureSubheading[locale]}</Description>
              <Spacer space="delta" />
              <StyledFieldGroup>
                <StyledFieldFull>
                  {fieldProps.fields.measureTitle}
                </StyledFieldFull>
                <StyledFieldFull>
                  {fieldProps.fields.measureDescription}
                </StyledFieldFull>
                <StyledFieldFull>
                  {fieldProps.fields.measureGoals}
                </StyledFieldFull>
                <StyledFieldFull>
                  {fieldProps.fields.measureContribution}
                </StyledFieldFull>
              </StyledFieldGroup>
              <Spacer space="giga" />
              <StyledHeading>
                {i18n.measureMoreInfoHeading[locale]}
              </StyledHeading>
              <Spacer space="gamma" />
              <Description>
                {i18n.measureMoreInfoSubheading[locale]}
              </Description>
              <Spacer space="delta" />
              <StyledFieldGroup>
                <StyledFieldFull>
                  {fieldProps.fields.measurePioneeringCharacter}
                </StyledFieldFull>
                <StyledFieldFull>
                  {fieldProps.fields.measureDuration}
                </StyledFieldFull>
                <StyledFieldFull>
                  {fieldProps.fields.measureOutput}
                </StyledFieldFull>
                <StyledFieldFull>
                  {fieldProps.fields.measureCommunication}
                </StyledFieldFull>
                <StyledFieldFull>
                  {fieldProps.fields.measurePitch}
                </StyledFieldFull>
                <StyledFieldFull>
                  {fieldProps.fields.measureImages}
                </StyledFieldFull>
                <StyledFieldFull>
                  {fieldProps.fields.measureComment}
                </StyledFieldFull>
                <StyledFieldFull>
                  {fieldProps.fields.charterConfirmed}
                </StyledFieldFull>
              </StyledFieldGroup>
              {message.text && (
                <>
                  <Spacer space="giga" />
                  <StyledMessage type={message.type}>
                    {message.text}
                  </StyledMessage>
                </>
              )}
              <Spacer space="giga" />
              <StyledButtonWrapper>
                <StyledButton
                  buttonText={i18n.submitButton[locale]}
                  type="submit"
                  onClick={() => actionProps.handleActionClick(onSubmit, true)}
                  disabled={isLoading}
                />
                {isLoading && <Spinner size={24} />}
              </StyledButtonWrapper>
            </>
          )}
        />
      </form>
    </StyledWrapper>
  );
};

export default MeasureForm;
